import React, { useEffect, useMemo, useState } from 'react';

import { Box, CssBaseline, ThemeProvider } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { QueryClientProvider } from '@tanstack/react-query';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import './pages/css/index.css';
import './components/ui.css';

import AppTopBar from './components/appTopBar';
import circularProgress from './components/circularProgress';
import { PageHeaderContext } from './components/pageHeaderContext';
import Sidebar from './components/sidebar';
import Login from './pages/login';
import Menu from './pages/menu';
import Unauthorized from './pages/unauthorized';
import RouteBICOB from './routes/biCOB';
import RouteBIGE from './routes/biGE';
import RouteChamados from './routes/chamados';
import RouteBISS from './routes/biSS';
import RouteCobranca from './routes/cobranca';
import RouteComercial from './routes/comercial';
import RouteConsultor from './routes/consultor';
import RouteCRM from './routes/crm';
import RouteFaturamento from './routes/faturamento';
import RouteFinanceiro from './routes/financeiro';
import RouteGE from './routes/gestaoEnergia';
import RouteOem from './routes/oem';
import RoutePedidosOeM from './routes/pedidos';
import RouteProtocolos from './routes/protocolos';
import RouteRelacionamento from './routes/relacionamento';
import RouteRI from './routes/ri';
import RouteParceiro from './routes/parceiro';
import RouteSettaServicos from './routes/settaServicos';
import { createAppTheme } from './theme';
import { ThemeModeContext } from './theme/themeModeContext';
import DadosUsuario from './utils/context';
import { fetchFlask } from './utils/fetchFlask';
import { getToken } from './utils/loginTokens';
import { queryClient } from './utils/queryClient';

const THEME_STORAGE_KEY = 'settaThemeMode';

const getInitialThemeMode = () => {
    const storedThemeMode = localStorage.getItem(THEME_STORAGE_KEY);
    return storedThemeMode === 'dark' ? 'dark' : 'light';
};

const App = () => {
    const [autorizacoes, setAutorizacoes] = useState([]);
    const [email, setEmail] = useState('');
    const [pageHeader, setPageHeader] = useState(null);
    const [themeMode, setThemeMode] = useState(getInitialThemeMode);
    const loginToken = getToken();
    const isMobile = useMediaQuery('(max-width:600px)');
    const appTheme = useMemo(() => createAppTheme(themeMode), [themeMode]);
    const pageHeaderContextValue = useMemo(() => ({ pageHeader, setPageHeader }), [pageHeader]);
    const themeContextValue = useMemo(() => ({
        mode: themeMode,
        isDarkMode: themeMode === 'dark',
        setThemeMode,
        toggleThemeMode: () => setThemeMode((currentMode) => currentMode === 'dark' ? 'light' : 'dark')
    }), [themeMode]);

    useEffect(() => {
        async function fetchAutorizacao () {
            const response = await fetchFlask('autorizacao');
            if (response.autorizacao === undefined) {
                alert(response.message);
            } else {
                setAutorizacoes(response.autorizacao);
                setEmail(response.email);
            }
        };
        if (loginToken) {
            fetchAutorizacao();
        }
    }, []);

    useEffect(() => {
        localStorage.setItem(THEME_STORAGE_KEY, themeMode);
        document.documentElement.dataset.appTheme = themeMode;
    }, [themeMode]);

    if (email === '' && loginToken !== null) {
        return circularProgress();
    }

    return (
        <QueryClientProvider client={queryClient}>
            <DadosUsuario.Provider value={{ autorizacoes, email }}>
                <Router>
                    {loginToken === null
                        ? <Login isMobile={isMobile}/>
                        : (<>
                            <ThemeModeContext.Provider value={themeContextValue}>
                                <ThemeProvider theme={appTheme}>
                                    <CssBaseline />
                                    <PageHeaderContext.Provider value={pageHeaderContextValue}>
                                        <Box className={`setta-app-shell app-theme-${themeMode}`}>
                                            {!isMobile && <Sidebar />}
                                            <AppTopBar />
                                            <Box component='main' className='setta-app-content'>
                                                <Routes>
                                                    <Route exact path='/' element={<Menu/>}/>
                                                    <Route path='/menu/:section' element={<Menu/>}/>
                                                    <Route path='/OeM/*' element={<RouteOem/>} />
                                                    <Route path='/Protocolos/*' element={<RouteProtocolos/>} />
                                                    <Route path='/GestaoEnergia/*' element={<RouteGE/>} />
                                                    <Route path='/Financeiro/*' element={<RouteFinanceiro/>} />
                                                    <Route path='/Comercial/*' element={<RouteComercial/>} />
                                                    <Route path='/Consultor/*' element={<RouteConsultor/>} />
                                                    <Route path='/settaServicos/*' element={<RouteSettaServicos/>} />
                                                    <Route path='/CRM/*' element={<RouteCRM isMobile={isMobile}/>} />
                                                    <Route path='/PedidosOeM/*' element={<RoutePedidosOeM/>} />
                                                    <Route path='/403-Unauthorized' element={<Unauthorized/>} />
                                                    <Route path='/Cobranca/*' element={<RouteCobranca/>} />
                                                    <Route path='/Relacionamento/*' element={<RouteRelacionamento/>} />
                                                    <Route path='/parceiro/*' element={<RouteParceiro/>} />
                                                    <Route path='/Faturamento/*' element={<RouteFaturamento/>} />
                                                    <Route path='/BISS/*' element={<RouteBISS/>} />
                                                    <Route path='/BIGE/*' element={<RouteBIGE/>} />
                                                    <Route path='/BICOB/*' element={<RouteBICOB/>} />
                                                    <Route path='/RI/*' element={<RouteRI/>} />
                                                    <Route path='/Chamados/*' element={<RouteChamados/>} />
                                                </Routes>
                                            </Box>
                                        </Box>
                                    </PageHeaderContext.Provider>
                                </ThemeProvider>
                            </ThemeModeContext.Provider>
                        </>)
                    }
                </Router>
            </DadosUsuario.Provider>
        </QueryClientProvider>
    );
};

export default App;
